import React, { useState } from 'react';
import {
  FileText,
  Users,
  Brain,
  Target,
  MessageSquare,
  Bookmark,
  Briefcase,
  Layers,
  Filter,
  Calendar,
  ChevronUp,
  ChevronDown,
  User,
  Globe,
  Image as ImageIcon,
  HelpCircle,
  FlaskConical,
} from 'lucide-react';
import { useAuth } from '../../../context/AuthContext';

interface SimulationData {
  id: number;
  source_model: string;
  created_at: string;
  personas: Array<{
    id: number;
    name: string;
    data: any;
  }>;
  segments?: Array<{
    id: number;
    name: string;
  }>;
}

interface ContentData {
  [key: string]: unknown;
  audience_name?: string;
  name?: string;
  goal?: string;
  task?: string;
  content_type?: string;
  segment_ids?: number[];
  persona_filters?: Record<string, Record<string, unknown>>;
  current_logo?: string;
  images?: string[];
  // Packaging Review V2 specific fields (nested in data object)
  data?: {
    brand?: string;
    product_context?: string;
    placement_type?: string;
    design_guideline?: string;
    analysis_type?: string;
    front_image?: string;
    side_images?: string[];
    back_images?: string[];
    competitor_images?: string[];
    packaging_image_descriptions?: string[];
    competitor_image_descriptions?: string[];
    [key: string]: unknown;
  };
}

interface SimulationInputsDropdownProps {
  simulation: SimulationData;
  contentData: ContentData;
  isOpen: boolean;
  onClose: () => void;
  onImageClick: (src: string, alt: string) => void;
  expandedCards: Record<string, boolean>;
  onToggleCard: (cardName: string) => void;
}

const SimulationInputsDropdown: React.FC<SimulationInputsDropdownProps> = ({
  simulation,
  contentData,
  isOpen,
  onClose,
  onImageClick,
  expandedCards,
  onToggleCard,
}) => {
  const { isAdmin } = useAuth();
  const [expandedDescriptions, setExpandedDescriptions] = useState<Record<string, boolean>>({});

  if (!isOpen || !contentData || !simulation) return null;

  const API_URL = import.meta.env.VITE_API_URL || '';

  const toggleDescription = (questionId: string, imageIndex: number) => {
    const key = `${questionId}-${imageIndex}`;
    setExpandedDescriptions(prev => ({ ...prev, [key]: !prev[key] }));
  };

  // Add model_used to contentData if user is admin
  const displayContentData = isAdmin
    ? { ...contentData, model_used: simulation.source_model || '' }
    : contentData;

  const getIconForKey = (key: string): JSX.Element => {
    switch (key) {
      case 'audience_id':
      case 'audience_name':
        return <Users className="h-4 w-4 text-blue-600" />;
      case 'task':
        return <Brain className="h-4 w-4 text-indigo-600" />;
      case 'name':
        return <FileText className="h-4 w-4 text-teal-600" />;
      case 'goal':
        return <Target className="h-4 w-4 text-green-600" />;
      case 'context':
      case 'product_context':
        return <MessageSquare className="h-4 w-4 text-amber-600" />;
      case 'content_type':
        return <FileText className="h-4 w-4 text-purple-600" />;
      case 'content_subject':
        return <Bookmark className="h-4 w-4 text-pink-600" />;
      case 'company_context':
        return <Briefcase className="h-4 w-4 text-gray-600" />;
      case 'segment_ids':
        return <Layers className="h-4 w-4 text-blue-600" />;
      case 'persona_filters':
        return <Filter className="h-4 w-4 text-orange-600" />;
      case 'brand':
        return <Bookmark className="h-4 w-4 text-indigo-600" />;
      case 'placement_type':
        return <Target className="h-4 w-4 text-purple-600" />;
      case 'design_guideline':
        return <Bookmark className="h-4 w-4 text-indigo-600" />;
      case 'product_name':
        return <FileText className="h-4 w-4 text-teal-600" />;
      case 'product_description':
        return <MessageSquare className="h-4 w-4 text-teal-600" />;
      case 'category':
        return <Layers className="h-4 w-4 text-amber-600" />;
      default:
        return <FileText className="h-4 w-4 text-blue-600" />;
    }
  };

  const extractPersonaInfo = (personaData: string | Record<string, unknown>) => {
    try {
      const data = typeof personaData === 'string' ? JSON.parse(personaData) : personaData;
      const extractField = (field: string, defaultValue: string = 'N/A'): string => {
        const value = data[field];
        if (value === undefined || value === null) return defaultValue;
        if (typeof value !== 'object') return String(value);
        try {
          if (Object.keys(value as Record<string, unknown>).length > 5) {
            return `Complex data (${Object.keys(value as Record<string, unknown>).length} properties)`;
          }
          return JSON.stringify(value, null, 2);
        } catch (error) {
          return 'Complex data';
        }
      };

      return {
        name: extractField('name', 'Unknown'),
        age: extractField('age'),
        job_title: extractField('job_title'),
        behavioral_archetype: extractField('behavioral_archetype'),
      };
    } catch (error) {
      return {
        name: 'Unknown',
        age: 'N/A',
        job_title: 'N/A',
        behavioral_archetype: 'N/A',
      };
    }
  };

  const formatValue = (key: string, value: unknown): React.ReactNode => {
    if (key === 'segment_ids' && Array.isArray(value)) {
      if (simulation?.segments) {
        return (
          <div className="flex flex-wrap gap-2 mt-2">
            {value.map((segmentId, idx) => {
              const segment = simulation.segments?.find((s) => s.id === segmentId);
              return (
                <span
                  key={idx}
                  className="px-3 py-1.5 bg-blue-50/80 text-blue-700 border border-blue-200 rounded-full text-xs font-medium flex items-center gap-1.5"
                >
                  <Globe className="h-3 w-3" />
                  {segment ? segment.name : `Segment ${segmentId}`}
                </span>
              );
            })}
          </div>
        );
      }
      return null;
    }

    if (key === 'persona_filters' && typeof value === 'object' && value !== null) {
      const personaFilters = value as Record<string, Record<string, unknown>>;
      return (
        <div className="mt-3 space-y-4">
          {Object.entries(personaFilters).length === 0 ? (
            <div className="text-sm text-gray-500 italic text-center p-3">
              No filters have been applied
            </div>
          ) : (
            Object.entries(personaFilters).map(([segmentId, filters], idx) => {
              const segmentName =
                simulation?.segments?.find((s) => s.id === parseInt(segmentId))?.name ||
                `Segment ${segmentId}`;
              const hasFilters = Object.entries(filters).some(
                ([, values]) => Array.isArray(values) && values.length > 0
              );

              if (!hasFilters) return null;

              return (
                <div
                  key={idx}
                  className="bg-white p-4 rounded-lg shadow-sm border border-green-100"
                >
                  <div className="text-sm font-medium text-green-800 mb-3 pb-2 border-b border-green-50 flex items-center">
                    <div className="bg-green-100 p-1.5 rounded-lg mr-2">
                      <Layers className="h-4 w-4 text-green-600" />
                    </div>
                    {segmentName}
                  </div>
                  <div className="space-y-3">
                    {Object.entries(filters)
                      .filter(
                        ([, filterValues]) =>
                          Array.isArray(filterValues) && filterValues.length > 0
                      )
                      .map(([filterKey, filterValues], fidx) => (
                        <div key={fidx} className="bg-green-50/50 rounded-lg p-3">
                          <div className="flex items-center mb-2">
                            <Filter className="h-3.5 w-3.5 mr-1.5 text-green-700" />
                            <span className="text-xs font-semibold text-green-800 uppercase">
                              {filterKey}
                            </span>
                          </div>
                          <div className="flex flex-wrap gap-2">
                            {(filterValues as string[]).map((val, vidx) => (
                              <span
                                key={vidx}
                                className="px-2.5 py-1 bg-white text-gray-700 border border-green-200 rounded-full text-xs font-medium shadow-sm"
                              >
                                {val}
                              </span>
                            ))}
                          </div>
                        </div>
                      ))}
                  </div>
                </div>
              );
            })
          )}
        </div>
      );
    }

    if (key === 'audience_id') return null;

    return (
      <span className="text-sm text-gray-800 font-medium">
        {typeof value === 'string' ? value : JSON.stringify(value)}
      </span>
    );
  };

  const isDriving = contentData.task === 'driving-concept-test';
  const isVitamin = contentData.task === 'vitamin-concept-test';
  const DRIVING_HIDDEN_KEYS = new Set(['llm_model', 'population']);
  const VITAMIN_HIDDEN_KEYS = new Set(['llm_model']);
  const externalHiddenKeys = isDriving ? DRIVING_HIDDEN_KEYS : isVitamin ? VITAMIN_HIDDEN_KEYS : null;

  // Detect if this is a packaging review simulation
  const isPackagingReview = contentData.task === 'packaging-review-v2' || 
    (contentData.data && (contentData.data.brand || contentData.data.product_context || contentData.data.placement_type));

  // Extract packaging data from nested data object
  const packagingData = isPackagingReview && contentData.data ? contentData.data : null;

  // Detect if this is a claims testing simulation
  const isClaimsTesting = contentData.task === 'claims_testing';
  const claimsData = isClaimsTesting && contentData.data ? contentData.data : null;

  const importantFields = [
    'name',
    ...(isAdmin ? ['model_used'] : []),
    'audience_name',
    'goal',
    'task',
    'content_type',
    'data',
  ];
  
  // Build important data - include use-case-specific fields extracted from nested data
  const baseImportantData = Object.fromEntries(
    Object.entries(displayContentData).filter(([key]) => importantFields.includes(key) && key !== 'data')
  );
  
  let importantData = baseImportantData;

  if (isPackagingReview && packagingData) {
    importantData = {
      ...baseImportantData,
      ...(packagingData.brand && { brand: packagingData.brand }),
      ...(packagingData.product_context && { product_context: packagingData.product_context }),
      ...(packagingData.placement_type && { placement_type: packagingData.placement_type }),
    };
  } else if (isClaimsTesting && claimsData) {
    const cd = claimsData as Record<string, unknown>;
    const claimsFields: Record<string, string> = {};
    if (cd.product_name) claimsFields.product_name = String(cd.product_name);
    if (cd.product_description) claimsFields.product_description = String(cd.product_description);
    if (cd.category) claimsFields.category = String(cd.category);
    if (cd.context) claimsFields.context = String(cd.context);
    importantData = { ...baseImportantData, ...claimsFields };
  }

  return (
    <div className="absolute z-[80] left-0 right-0 mb-5 mx-6 bg-white rounded-lg shadow-lg border border-gray-200 overflow-hidden">
      <div className="max-h-[70vh] overflow-y-auto">
        {/* Header */}
        <div className="p-4 bg-gradient-to-r from-blue-50 to-indigo-50 border-b border-blue-100">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <div className="mr-3 bg-blue-500 p-2 rounded-full text-white">
                <FileText className="h-5 w-5" />
              </div>
              <h2 className="text-lg font-semibold text-blue-800">Simulation Inputs</h2>
            </div>
            <div className="flex items-center">
              <span className="text-xs text-gray-600 mr-3">
                {new Date(simulation.created_at).toLocaleString()}
              </span>
              <button
                onClick={onClose}
                className="p-1.5 hover:bg-gray-100 rounded-full text-gray-500"
                aria-label="Close summary"
              >
                <ChevronUp className="h-5 w-5" />
              </button>
            </div>
          </div>
        </div>

        {/* Main content area */}
        <div className="p-5">
          {/* Key information grid */}
          <div className="grid grid-cols-2 gap-4 mb-6">
            {Object.entries(importantData).filter(([key]) => !(externalHiddenKeys && externalHiddenKeys.has(key))).map(([key, value]: [string, unknown]) => (
              <div
                key={key}
                className="flex items-center bg-blue-50/50 p-3 rounded-lg border border-blue-100"
              >
                <div className="mr-3 bg-blue-100 p-2 rounded-full">
                  {getIconForKey(key === 'model_used' ? 'source_model' : key)}
                </div>
                <div>
                  <p className="text-xs font-semibold text-blue-700 uppercase tracking-wider">
                    {key === 'model_used' ? 'Model Used' : key.replace(/_/g, ' ')}
                  </p>
                  <p className="text-sm font-medium text-gray-800">
                    {typeof value === 'string' ? value : String(value || '')}
                  </p>
                </div>
              </div>
            ))}
          </div>

          {/* Segments section */}
          {contentData.segment_ids && (
            <div className="mb-6">
              <div className="flex items-center mb-2">
                <div className="mr-2 bg-green-100 p-1.5 rounded-md">
                  <Layers className="h-4 w-4 text-green-600" />
                </div>
                <h3 className="text-base font-medium text-green-800">Audience Segments</h3>
              </div>
              <div className="bg-green-50/30 p-3 rounded-lg border border-green-100">
                {formatValue('segment_ids', contentData.segment_ids) as React.ReactNode}
              </div>
            </div>
          )}

          {/* Filters section - Temporarily disabled due to TypeScript unknown type issue */}
          {/* TODO: Fix persona_filters typing in ContentData interface */}

          {/* Current Logo */}
          {contentData.current_logo && (
            <div className="mb-6">
              <div className="flex items-center mb-2">
                <h3 className="text-base font-medium text-blue-800">
                  Current Logo (Reference)
                </h3>
              </div>
              <div className="bg-blue-50/30 p-3 rounded-lg border border-blue-200">
                <div className="relative inline-block">
                  <img
                    src={contentData.current_logo}
                    alt="Current logo for reference"
                    className="h-32 w-auto object-contain rounded-lg cursor-pointer hover:opacity-90 transition-opacity"
                    onClick={() =>
                      onImageClick(contentData.current_logo!, 'Current logo for reference')
                    }
                  />
                  <span className="absolute -bottom-2 left-0 right-0 text-center">
                    <span className="bg-blue-100 text-blue-800 text-xs px-2 py-1 rounded">
                      Reference Only
                    </span>
                  </span>
                </div>
              </div>
            </div>
          )}

          {/* Images - show different header based on task type */}
          {contentData.images && contentData.images.length > 0 && (
            <div className="mb-6">
              <div className="flex items-center mb-2">
                <h3 className="text-base font-medium text-purple-800">
                  {contentData.task === 'logo_test' || contentData.task === 'logo-test' 
                    ? 'Logo Concepts to Test' 
                    : contentData.task === 'image-survey'
                    ? 'Images for Click/Rank Test'
                    : 'Test Images'}
                </h3>
              </div>
              <div className="bg-purple-50/30 p-3 rounded-lg border border-purple-100">
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  {contentData.images.map((imageData, index) => (
                    <img
                      key={index}
                      src={imageData}
                      alt={`Image ${String.fromCharCode(65 + index)}`}
                      className="h-32 w-full object-contain rounded-lg cursor-pointer hover:opacity-90 transition-opacity border border-purple-200 bg-white p-2"
                      onClick={() =>
                        onImageClick(
                          imageData,
                          `Image ${String.fromCharCode(65 + index)}`
                        )
                      }
                    />
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Front Panel Image */}
          {packagingData?.front_image && (
            <div className="mb-6">
              <div className="flex items-center mb-2">
                <div className="mr-2 bg-teal-100 p-1.5 rounded-md">
                  <ImageIcon className="h-4 w-4 text-teal-600" />
                </div>
                <h3 className="text-base font-medium text-teal-800">Front Panel Image</h3>
              </div>
              <div className="bg-teal-50/30 p-3 rounded-lg border border-teal-100">
                <div className="relative bg-white rounded-lg border border-teal-200 overflow-hidden inline-block">
                  <img
                    src={packagingData.front_image}
                    alt="Front Panel"
                    className="h-32 w-auto object-contain cursor-pointer hover:opacity-90 transition-opacity p-2"
                    onClick={() => onImageClick(packagingData.front_image!, 'Front Panel')}
                  />
                  <div className="absolute top-2 left-2 bg-teal-600 text-white text-xs px-2 py-1 rounded-full font-semibold">
                    F1
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Side Panel Images */}
          {packagingData?.side_images && Array.isArray(packagingData.side_images) && packagingData.side_images.length > 0 && (
            <div className="mb-6">
              <div className="flex items-center mb-2">
                <div className="mr-2 bg-blue-100 p-1.5 rounded-md">
                  <ImageIcon className="h-4 w-4 text-blue-600" />
                </div>
                <h3 className="text-base font-medium text-blue-800">Side Panel Images ({packagingData.side_images.length})</h3>
              </div>
              <div className="bg-blue-50/30 p-3 rounded-lg border border-blue-100">
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  {packagingData.side_images.map((imageData, index) => (
                    <div key={index} className="relative bg-white rounded-lg border border-blue-200 overflow-hidden">
                      <img
                        src={imageData}
                        alt={`Side Panel ${index + 1}`}
                        className="h-32 w-full object-contain cursor-pointer hover:opacity-90 transition-opacity p-2"
                        onClick={() => onImageClick(imageData, `Side Panel ${index + 1}`)}
                      />
                      <div className="absolute top-2 left-2 bg-blue-600 text-white text-xs px-2 py-1 rounded-full font-semibold">
                        S{index + 1}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Back Panel Images */}
          {packagingData?.back_images && Array.isArray(packagingData.back_images) && packagingData.back_images.length > 0 && (
            <div className="mb-6">
              <div className="flex items-center mb-2">
                <div className="mr-2 bg-purple-100 p-1.5 rounded-md">
                  <ImageIcon className="h-4 w-4 text-purple-600" />
                </div>
                <h3 className="text-base font-medium text-purple-800">Back Panel Images ({packagingData.back_images.length})</h3>
              </div>
              <div className="bg-purple-50/30 p-3 rounded-lg border border-purple-100">
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  {packagingData.back_images.map((imageData, index) => (
                    <div key={index} className="relative bg-white rounded-lg border border-purple-200 overflow-hidden">
                      <img
                        src={imageData}
                        alt={`Back Panel ${index + 1}`}
                        className="h-32 w-full object-contain cursor-pointer hover:opacity-90 transition-opacity p-2"
                        onClick={() => onImageClick(imageData, `Back Panel ${index + 1}`)}
                      />
                      <div className="absolute top-2 left-2 bg-purple-600 text-white text-xs px-2 py-1 rounded-full font-semibold">
                        B{index + 1}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Competitor Images */}
          {packagingData?.competitor_images && Array.isArray(packagingData.competitor_images) && packagingData.competitor_images.length > 0 && (
            <div className="mb-6">
              <div className="flex items-center mb-2">
                <div className="mr-2 bg-orange-100 p-1.5 rounded-md">
                  <ImageIcon className="h-4 w-4 text-orange-600" />
                </div>
                <h3 className="text-base font-medium text-orange-800">Competitor Images ({packagingData.competitor_images.length})</h3>
              </div>
              <div className="bg-orange-50/30 p-3 rounded-lg border border-orange-100">
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  {packagingData.competitor_images.map((imageData, index) => (
                    <div key={index} className="relative bg-white rounded-lg border border-orange-200 overflow-hidden">
                      <img
                        src={imageData}
                        alt={`Competitor ${index + 1}`}
                        className="h-32 w-full object-contain cursor-pointer hover:opacity-90 transition-opacity p-2"
                        onClick={() => onImageClick(imageData, `Competitor ${index + 1}`)}
                      />
                      <div className="absolute top-2 left-2 bg-orange-600 text-white text-xs px-2 py-1 rounded-full font-semibold">
                        C{index + 1}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Design Guideline (Packaging specific) */}
          {packagingData?.design_guideline && (
            <div className="mb-6">
              <div className="flex items-center mb-2">
                <div className="mr-2 bg-indigo-100 p-1.5 rounded-md">
                  <Bookmark className="h-4 w-4 text-indigo-600" />
                </div>
                <h3 className="text-base font-medium text-indigo-800">Design Guidelines</h3>
              </div>
              <div className="bg-indigo-50/30 p-4 rounded-lg border border-indigo-100">
                <p className="text-sm text-gray-800 whitespace-pre-wrap">{packagingData.design_guideline}</p>
              </div>
            </div>
          )}

          {/* Claims List (Claims Testing specific) */}
          {claimsData && Array.isArray(claimsData.claims) && (claimsData.claims as string[]).length > 0 && (
            <div className="mb-6 border border-amber-100 rounded-lg overflow-hidden">
              <div
                className="flex items-center justify-between p-3 bg-gradient-to-r from-amber-50 to-amber-50/70 cursor-pointer"
                onClick={() => onToggleCard('claims')}
              >
                <div className="flex items-center">
                  <div className="mr-2 bg-amber-100 p-1.5 rounded-md">
                    <FlaskConical className="h-4 w-4 text-amber-600" />
                  </div>
                  <h3 className="text-base font-medium text-amber-800">
                    Claims ({(claimsData.claims as string[]).length})
                  </h3>
                </div>
                <div className="text-amber-600">
                  {expandedCards.claims ? (
                    <ChevronUp className="h-5 w-5" />
                  ) : (
                    <ChevronDown className="h-5 w-5" />
                  )}
                </div>
              </div>

              {expandedCards.claims && (
                <div className="p-4 bg-white">
                  <div className="space-y-2">
                    {(claimsData.claims as string[]).map((claim: string, idx: number) => (
                      <div
                        key={idx}
                        className="flex items-start bg-amber-50/40 p-3 rounded-lg border border-amber-100"
                      >
                        <span className="text-amber-600 font-mono text-xs font-bold mr-3 mt-0.5 bg-amber-100 px-2 py-0.5 rounded">
                          {idx + 1}
                        </span>
                        <span className="text-sm text-gray-800">{claim}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Survey Questions - COLLAPSIBLE */}
          {contentData.questions && Array.isArray(contentData.questions) && contentData.questions.length > 0 && (
            <div className="mb-6 border border-teal-100 rounded-lg overflow-hidden">
              <div
                className="flex items-center justify-between p-3 bg-gradient-to-r from-teal-50 to-teal-50/70 cursor-pointer"
                onClick={() => onToggleCard('questions')}
              >
                <div className="flex items-center">
                  <div className="mr-2 bg-teal-100 p-1.5 rounded-md">
                    <HelpCircle className="h-4 w-4 text-teal-600" />
                  </div>
                  <h3 className="text-base font-medium text-teal-800">
                    Survey Questions ({contentData.questions.length})
                  </h3>
                </div>
                <div className="text-teal-600">
                  {expandedCards.questions ? (
                    <ChevronUp className="h-5 w-5" />
                  ) : (
                    <ChevronDown className="h-5 w-5" />
                  )}
                </div>
              </div>

              {expandedCards.questions && (
                <div className="p-4 bg-white space-y-4">
                  {contentData.questions.map((question: any, index: number) => (
                    <div
                      key={question.id || index}
                      className="bg-teal-50/30 p-4 rounded-lg border border-teal-100"
                    >
                      {/* Question Header */}
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex-1">
                          <div className="flex items-center mb-2">
                            <span className="bg-teal-100 text-teal-700 text-xs font-bold px-2 py-1 rounded mr-2">
                              {question.id}
                            </span>
                            <span className="text-xs text-teal-600 font-medium uppercase">
                              {question.type?.replace(/_/g, ' ')}
                            </span>
                          </div>
                          <p className="text-sm font-medium text-gray-800">{question.text}</p>
                        </div>
                      </div>

                      {/* Question Images - only show if attached at question level */}
                      {question.image_paths && question.image_paths.length > 0 && question.image_attachment_level !== 'option' && (
                        <div className="mt-3 pt-3 border-t border-teal-100">
                          <div className="flex items-center mb-2">
                            <ImageIcon className="h-3.5 w-3.5 text-teal-600 mr-1.5" />
                            <span className="text-xs font-semibold text-teal-700">
                              {question.image_paths.length} {question.image_paths.length === 1 ? 'Image' : 'Images'} (Context)
                            </span>
                          </div>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                            {question.image_paths.map((imagePath: string, imgIndex: number) => {
                              const fullImageUrl = imagePath.startsWith('http') ? imagePath : `${API_URL}${imagePath}`;
                              const imageDescription = question.image_descriptions?.[imgIndex];
                              const descKey = `${question.id}-${imgIndex}`;
                              const isDescExpanded = expandedDescriptions[descKey];

                              return (
                                <div key={imgIndex} className="bg-white rounded-lg border border-teal-200 overflow-hidden">
                                  {/* Image */}
                                  <img
                                    src={fullImageUrl}
                                    alt={`Question ${question.id} - Image ${imgIndex + 1}`}
                                    className="w-full h-48 object-cover cursor-pointer hover:opacity-90 transition-opacity"
                                    onClick={() => onImageClick(fullImageUrl, `Question ${question.id} - Image ${imgIndex + 1}`)}
                                  />
                                  
                                  {/* AI Description */}
                                  {imageDescription && (
                                    <div className="p-3 bg-gray-50">
                                      <div className="flex items-center justify-between mb-1">
                                        <span className="text-xs font-semibold text-gray-600 uppercase flex items-center">
                                          <Brain className="h-3 w-3 mr-1" />
                                          AI Description
                                        </span>
                                        <button
                                          onClick={() => toggleDescription(question.id, imgIndex)}
                                          className="text-xs text-teal-600 hover:text-teal-700 font-medium"
                                        >
                                          {isDescExpanded ? 'Show Less' : 'Show More'}
                                        </button>
                                      </div>
                                      <p className={`text-xs text-gray-700 italic ${isDescExpanded ? '' : 'line-clamp-2'}`}>
                                        {imageDescription}
                                      </p>
                                    </div>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      )}

                      {/* Question Choices */}
                      {question.choices && question.choices.length > 0 && (
                        <div className="mt-3 pt-3 border-t border-teal-100">
                          <span className="text-xs font-semibold text-teal-700 uppercase block mb-2">
                            Answer Choices
                            {question.option_images && Object.keys(question.option_images).length > 0 && (
                              <span className="ml-2 text-blue-600 font-normal lowercase">(with images)</span>
                            )}
                          </span>
                          {question.option_images && Object.keys(question.option_images).length > 0 ? (
                            // Display choices with images - horizontal display with numbered badges
                            <div className="flex flex-wrap gap-2 mt-2">
                              {question.choices.map((choice: string, idx: number) => {
                                const imageUrl = question.option_images?.[choice];
                                const fullImageUrl = imageUrl ? (imageUrl.startsWith('http') ? imageUrl : `${API_URL}${imageUrl}`) : null;
                                
                                return (
                                  <div key={idx} className="relative">
                                    <div className="absolute top-1 left-1 bg-teal-900 bg-opacity-75 text-white text-xs font-bold rounded-full w-6 h-6 flex items-center justify-center z-10">
                                      {idx + 1}
                                    </div>
                                    {fullImageUrl ? (
                                      <img
                                        src={fullImageUrl}
                                        alt={`Option ${idx + 1}`}
                                        className="h-24 w-24 object-cover rounded border-2 border-teal-200 cursor-pointer hover:opacity-90"
                                        onClick={() => onImageClick(fullImageUrl, `Option ${idx + 1}`)}
                                      />
                                    ) : (
                                      <div className="h-24 w-24 flex items-center justify-center bg-gray-100 rounded border-2 border-gray-300">
                                        <span className="text-xs text-gray-400">N/A</span>
                                      </div>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          ) : (
                            // Regular text-only choices (pills)
                            <div className="flex flex-wrap gap-2">
                              {question.choices.map((choice: string, idx: number) => (
                                <span
                                  key={idx}
                                  className="px-2.5 py-1 bg-white text-gray-700 border border-teal-200 rounded-full text-xs"
                                >
                                  {idx + 1}. {choice}
                                </span>
                              ))}
                            </div>
                          )}
                        </div>
                      )}

                      {/* Scale Info */}
                      {question.scale_min !== null && question.scale_max !== null && (
                        <div className="mt-3 pt-3 border-t border-teal-100">
                          <span className="text-xs font-semibold text-teal-700 uppercase block mb-1">
                            Scale Range
                          </span>
                          <span className="text-xs text-gray-700">
                            {question.scale_min} to {question.scale_max}
                          </span>
                        </div>
                      )}

                      {/* MaxDiff Items */}
                      {question.maxdiff_items && question.maxdiff_items.length > 0 && (
                        <div className="mt-3 pt-3 border-t border-teal-100">
                          <span className="text-xs font-semibold text-teal-700 uppercase block mb-2">
                            MaxDiff Items ({question.maxdiff_items.length})
                            <span className="ml-2 text-blue-600 font-normal lowercase">
                              (4 per set, {Math.ceil((question.maxdiff_items.length * 4) / 4)} sets per persona)
                            </span>
                          </span>
                          <div className="flex flex-wrap gap-2">
                            {question.maxdiff_items.map((item: string, idx: number) => (
                              <span
                                key={idx}
                                className="px-2.5 py-1 bg-white text-gray-700 border border-teal-200 rounded-full text-xs"
                              >
                                {item}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Conjoint Attributes */}
                      {question.conjoint_attributes && question.conjoint_attributes.length > 0 && (
                        <div className="mt-3 pt-3 border-t border-teal-100">
                          <span className="text-xs font-semibold text-teal-700 uppercase block mb-2">
                            Conjoint Attributes
                          </span>
                          <div className="space-y-2">
                            {question.conjoint_attributes.map((attr: any, idx: number) => (
                              <div key={idx} className="bg-white p-2 rounded border border-teal-200">
                                <div className="text-xs font-medium text-gray-800 mb-1">
                                  {attr.name}
                                </div>
                                <div className="flex flex-wrap gap-1">
                                  {attr.levels.map((level: string, lidx: number) => (
                                    <span
                                      key={lidx}
                                      className="px-2 py-0.5 bg-teal-50 text-teal-700 rounded text-xs"
                                    >
                                      {level}
                                    </span>
                                  ))}
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Matrix Rows (Items to Rate) */}
                      {question.matrix_rows && question.matrix_rows.length > 0 && (
                        <div className="mt-3 pt-3 border-t border-teal-100">
                          <span className="text-xs font-semibold text-teal-700 uppercase block mb-2">
                            Items to Rate ({question.matrix_rows.length})
                          </span>
                          <div className="flex flex-wrap gap-2">
                            {question.matrix_rows.map((item: string, idx: number) => (
                              <span
                                key={idx}
                                className="px-2.5 py-1 bg-white text-gray-700 border border-teal-200 rounded-full text-xs"
                              >
                                {idx + 1}. {item}
                              </span>
                            ))}
                          </div>
                          {/* Matrix Columns (Rating Scale Labels) */}
                          {question.matrix_columns && question.matrix_columns.length > 0 && (
                            <div className="mt-2 pt-2 border-t border-teal-50">
                              <span className="text-xs font-medium text-teal-600 block mb-1">
                                Scale: {question.matrix_columns.join(' → ')}
                              </span>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Used Personas - COLLAPSIBLE */}
          {simulation.personas && simulation.personas.length > 0 && (
            <div className="mb-6 border border-purple-100 rounded-lg overflow-hidden">
              <div
                className="flex items-center justify-between p-3 bg-gradient-to-r from-purple-50 to-purple-50/70 cursor-pointer"
                onClick={() => onToggleCard('personas')}
              >
                <div className="flex items-center">
                  <div className="mr-2 bg-purple-100 p-1.5 rounded-md">
                    <Users className="h-4 w-4 text-purple-600" />
                  </div>
                  <h3 className="text-base font-medium text-purple-800">
                    Used Profiles ({simulation.personas.length})
                  </h3>
                </div>
                <div className="text-purple-600">
                  {expandedCards.personas ? (
                    <ChevronUp className="h-5 w-5" />
                  ) : (
                    <ChevronDown className="h-5 w-5" />
                  )}
                </div>
              </div>

              {expandedCards.personas && (
                <div className="p-3 bg-white">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {simulation.personas.map((persona, index) => {
                      const personaInfo = extractPersonaInfo(persona.data);
                      return (
                        <div
                          key={index}
                          className="bg-purple-50/40 p-3 rounded-lg border border-purple-100"
                        >
                          <div className="flex justify-between items-start mb-2">
                            <div className="flex items-center space-x-2">
                              <div className="bg-purple-100 p-1 rounded-full">
                                <User className="h-3.5 w-3.5 text-purple-600" />
                              </div>
                              <p className="font-medium text-gray-800">{personaInfo.name}</p>
                            </div>
                            <div className="px-2 py-0.5 bg-purple-100 text-purple-700 rounded-full text-xs font-medium">
                              {persona.name ? persona.name.replace(/_/g, ' ') : 'Profile'}
                            </div>
                          </div>
                          <div className="grid grid-cols-2 gap-2 mt-2 border-t border-purple-100/60 pt-2 text-xs">
                            <div className="flex items-center space-x-1.5">
                              <Calendar className="h-3 w-3 text-purple-600/70" />
                              <p>
                                <span className="text-gray-600">Age:</span>{' '}
                                <span className="text-gray-900 font-medium">
                                  {personaInfo.age}
                                </span>
                              </p>
                            </div>
                            <div className="flex items-center space-x-1.5">
                              <Briefcase className="h-3 w-3 text-purple-600/70" />
                              <p>
                                <span className="text-gray-600">Role:</span>{' '}
                                <span className="text-gray-900 font-medium">
                                  {personaInfo.job_title}
                                </span>
                              </p>
                            </div>
                            {personaInfo.behavioral_archetype !== 'N/A' && (
                              <div className="col-span-2 flex items-center space-x-1.5">
                                <Brain className="h-3 w-3 text-purple-600/70" />
                                <p>
                                  <span className="text-gray-600">Archetype:</span>{' '}
                                  <span className="text-gray-900 font-medium">
                                    {personaInfo.behavioral_archetype}
                                  </span>
                                </p>
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Additional details */}
          {Object.entries(displayContentData).filter(
            ([key]) =>
              !importantFields.includes(key) &&
              key !== 'segment_ids' &&
              key !== 'persona_filters' &&
              key !== 'images' &&
              key !== 'audience_id' &&
              key !== 'image_descriptions' &&
              key !== 'current_logo' &&
              key !== 'questions' &&
              !(externalHiddenKeys && externalHiddenKeys.has(key))
          ).length > 0 && (
            <div className="mb-3">
              <div className="flex items-center mb-2">
                <div className="mr-2 bg-gray-100 p-1.5 rounded-md">
                  <MessageSquare className="h-4 w-4 text-gray-600" />
                </div>
                <h3 className="text-base font-medium text-gray-700">
                  Additional Information
                </h3>
              </div>
              <div className="bg-gray-50/70 p-3 rounded-lg border border-gray-200">
                <div className="space-y-3">
                  {Object.entries(displayContentData)
                    .filter(
                      ([key]) =>
                        !importantFields.includes(key) &&
                        key !== 'segment_ids' &&
                        key !== 'persona_filters' &&
                        key !== 'images' &&
                        key !== 'audience_id' &&
                        key !== 'image_descriptions' &&
                        key !== 'current_logo' &&
                        key !== 'questions' &&
                        !(externalHiddenKeys && externalHiddenKeys.has(key))
                    )
                    .map(([key, value], idx) => (
                      <div
                        key={idx}
                        className="flex flex-col bg-white p-3 rounded border border-gray-100"
                      >
                        <div className="flex items-center mb-1.5">
                          <div className="mr-2 bg-gray-100 p-1 rounded">
                            {getIconForKey(key)}
                          </div>
                          <p className="text-xs font-semibold text-gray-700 uppercase tracking-wider">
                            {key.replace(/_/g, ' ')}
                          </p>
                        </div>
                        <div className="text-sm ml-7">{formatValue(key, value) as React.ReactNode}</div>
                      </div>
                    ))}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-3 bg-gray-50 border-t mb-10 border-gray-200 flex justify-end">
          <button
            onClick={onClose}
            className="px-3 py-1.5 bg-white border border-gray-300 rounded text-sm font-medium text-gray-600 hover:bg-gray-50"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default SimulationInputsDropdown;

